import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { AlertCircle, ExternalLink, MoreVertical, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type Control, type FieldPath, useForm } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateSource,
  useDeleteSource,
  useSources,
  useUpdateSource,
} from "@/hooks/admin/sources";
import { AdminLayout } from "@/layouts/AdminLayout";
import { cn } from "@/lib/utils";
import type { LicenceStatus, RefreshStrategy, Source, SourceCategory, SourceType } from "@/types";

const sourceFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  url: z
    .string()
    .trim()
    .refine((v) => v === "" || /^https?:\/\/.+/i.test(v), "Must be a valid http(s) URL"),
  endpoint: z.string().trim(),
  category: z.string(),
  access_type: z.string().trim(),
  source_type: z.string(),
  licence_status: z.string(),
  allowed_use: z.string(),
  suggested_refresh: z.string(),
  refresh_cron: z.string(),
  refresh_strategy: z.string(),
  best_coverage: z.string(),
  topic_generator_use: z.string(),
  legal_notes: z.string(),
  is_active: z.boolean(),
});
type SourceFormValues = z.infer<typeof sourceFormSchema>;

const EMPTY_SOURCE: SourceFormValues = {
  name: "",
  url: "",
  endpoint: "",
  category: "",
  access_type: "",
  source_type: "",
  licence_status: "",
  allowed_use: "",
  suggested_refresh: "",
  refresh_cron: "",
  refresh_strategy: "",
  best_coverage: "",
  topic_generator_use: "",
  legal_notes: "",
  is_active: true,
};

function toFormValues(s: Source): SourceFormValues {
  return {
    name: s.name,
    url: s.url ?? "",
    endpoint: s.endpoint ?? "",
    category: s.category ?? "",
    access_type: s.access_type ?? "",
    source_type: s.source_type ?? "",
    licence_status: s.licence_status ?? "",
    allowed_use: s.allowed_use ?? "",
    suggested_refresh: s.suggested_refresh ?? "",
    refresh_cron: s.refresh_cron ?? "",
    refresh_strategy: s.refresh_strategy ?? "",
    best_coverage: s.best_coverage ?? "",
    topic_generator_use: s.topic_generator_use ?? "",
    legal_notes: s.legal_notes ?? "",
    is_active: s.is_active,
  };
}

// Radix Select forbids an empty-string item value, so use a sentinel for "none".
const NONE = "__none__";
type FieldName = FieldPath<SourceFormValues>;
type Option = string | { value: string; label: string };

function TextField({
  control,
  name,
  label,
  required,
}: {
  control: Control<SourceFormValues>;
  name: FieldName;
  label: string;
  required?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label}
            {required ? <span className="text-red-500"> *</span> : null}
          </FormLabel>
          <FormControl>
            <Input {...field} value={String(field.value ?? "")} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function TextareaField({
  control,
  name,
  label,
}: {
  control: Control<SourceFormValues>;
  name: FieldName;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea {...field} value={String(field.value ?? "")} rows={3} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function SelectField({
  control,
  name,
  label,
  options,
}: {
  control: Control<SourceFormValues>;
  name: FieldName;
  label: string;
  options: Option[];
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select
            value={field.value ? String(field.value) : NONE}
            onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="— select —" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value={NONE}>— none —</SelectItem>
              {options.map((o) => {
                const value = typeof o === "string" ? o : o.value;
                const text = typeof o === "string" ? o : o.label;
                return (
                  <SelectItem key={value} value={value}>
                    {text}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function CheckboxField({
  control,
  name,
  label,
}: {
  control: Control<SourceFormValues>;
  name: FieldName;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={Boolean(field.value)}
              onCheckedChange={(c) => field.onChange(c === true)}
            />
            <span className="text-sm text-gray-700">
              {label} — {field.value ? "Active" : "Inactive"}
            </span>
          </label>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

const SOURCE_CATEGORIES: { value: string; label: string }[] = [
  { value: "free_financial_news", label: "Free Financial News" },
  { value: "official_macro_data", label: "Official Macro Data" },
  { value: "market_data_apis", label: "Market Data APIs" },
  { value: "fx_and_rates", label: "FX & Rates" },
  { value: "commodities_energy", label: "Commodities & Energy" },
  { value: "digital_assets", label: "Digital Assets" },
  { value: "real_estate", label: "Real Estate" },
  { value: "policy_regulation_esg", label: "Policy, Regulation & ESG" },
  { value: "aggregators_social", label: "Aggregators & Social" },
];
const SOURCE_TYPES = ["rss", "api", "html", "xml_feed", "csv_download", "pdf", "aggregator"];
const LICENCE_STATUSES = ["free", "licensed", "paywalled", "unknown"];
const REFRESH_STRATEGIES = ["scheduled", "release_based", "event_based", "on_demand", "every_run"];
const SOURCE_CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  SOURCE_CATEGORIES.map((c) => [c.value, c.label])
);

const PAGE_SIZE = 25;

function SourceFormModal({
  source,
  onClose,
  onSaved,
}: {
  source?: Source;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createSource = useCreateSource();
  const updateSource = useUpdateSource();
  const form = useForm<SourceFormValues>({
    resolver: zodResolver(sourceFormSchema),
    defaultValues: source ? toFormValues(source) : EMPTY_SOURCE,
  });
  const { control } = form;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const nn = (v: string) => (v.trim() ? v.trim() : null);
    const payload: Partial<Source> = {
      name: values.name.trim(),
      url: nn(values.url),
      endpoint: nn(values.endpoint),
      category: nn(values.category) as SourceCategory | null,
      access_type: nn(values.access_type),
      source_type: nn(values.source_type) as SourceType | null,
      licence_status: nn(values.licence_status) as LicenceStatus | null,
      allowed_use: nn(values.allowed_use),
      suggested_refresh: nn(values.suggested_refresh),
      refresh_cron: nn(values.refresh_cron),
      refresh_strategy: nn(values.refresh_strategy) as RefreshStrategy | null,
      best_coverage: nn(values.best_coverage),
      topic_generator_use: nn(values.topic_generator_use),
      legal_notes: nn(values.legal_notes),
      is_active: values.is_active,
    };
    try {
      if (source) {
        await updateSource.mutateAsync({ id: source.id, data: payload });
      } else {
        await createSource.mutateAsync(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save source");
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 text-xl">
            {source ? "Edit Source" : "Add Source"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}
            <div className="space-y-4">
              <TextField control={control} name="name" label="Name" required />
              <TextField control={control} name="url" label="URL" />
              <TextField control={control} name="endpoint" label="Endpoint (API/RSS/fetch URL)" />
              <SelectField
                control={control}
                name="category"
                label="Category"
                options={SOURCE_CATEGORIES}
              />
              <TextField control={control} name="access_type" label="Access type" />
              <SelectField
                control={control}
                name="source_type"
                label="Type"
                options={SOURCE_TYPES}
              />
              <SelectField
                control={control}
                name="licence_status"
                label="Licence status"
                options={LICENCE_STATUSES}
              />
              <TextareaField control={control} name="allowed_use" label="Allowed use" />
              <TextField control={control} name="suggested_refresh" label="Suggested refresh" />
              <TextField control={control} name="refresh_cron" label="Refresh cron" />
              <SelectField
                control={control}
                name="refresh_strategy"
                label="Refresh strategy"
                options={REFRESH_STRATEGIES}
              />
              <TextareaField control={control} name="best_coverage" label="Best coverage" />
              <TextareaField
                control={control}
                name="topic_generator_use"
                label="Topic generator use"
              />
              <TextareaField control={control} name="legal_notes" label="Legal notes" />
              <CheckboxField control={control} name="is_active" label="Active" />
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-primary px-4 py-2 font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? "Saving…" : source ? "Save Changes" : "Add Source"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

function DeleteSourceModal({
  open,
  source,
  onClose,
  onDeleted,
}: {
  open: boolean;
  source: Source | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const deleteSource = useDeleteSource();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  if (!open || !source) return null;

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteSource.mutateAsync(source.id);
      onDeleted();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete source";
      try {
        const detail = JSON.parse(msg);
        if (detail.dependent_count) {
          setError(
            `Cannot delete — this source is referenced by ${detail.dependent_count} record(s) (${detail.topic_count ?? 0} topics, ${detail.seed_event_count ?? 0} seed events).`
          );
          return;
        }
      } catch {}
      setError(msg);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white text-gray-900 rounded-2xl w-full max-w-md shadow-2xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Source</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{source.name}</strong>? This cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteSource.isPending}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
          >
            {deleteSource.isPending ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

type ActiveFilter = "all" | "active";

export function AdminSourcesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [sourceModal, setSourceModal] = useState<{ open: boolean; source?: Source }>({
    open: false,
  });
  const [deletingSource, setDeletingSource] = useState<Source | null>(null);

  const sort = sorting[0];
  const sourcesQuery = useSources({
    page,
    page_size: PAGE_SIZE,
    active_only: activeFilter === "active",
    q: search || undefined,
    sort_by: sort?.id,
    sort_order: sort ? (sort.desc ? "desc" : "asc") : undefined,
  });
  const data = sourcesQuery.data ?? null;
  const error = sourcesQuery.isError
    ? sourcesQuery.error instanceof Error
      ? sourcesQuery.error.message
      : "Failed to load sources"
    : null;

  const { mutate: mutateUpdateSource } = useUpdateSource();

  const columns = useMemo<ColumnDef<Source>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const source = row.original;
          return (
            <div>
              <div className="font-medium text-gray-900">{source.name}</div>
              {source.url && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 flex items-center gap-1 text-xs text-green-600 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  {source.url}
                </a>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) =>
          row.original.category ? (
            <StatusBadge tone="info">
              {SOURCE_CATEGORY_LABEL[row.original.category] ?? row.original.category}
            </StatusBadge>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          ),
      },
      {
        accessorKey: "source_type",
        header: "Type",
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.source_type ?? "—"}</span>
        ),
      },
      {
        accessorKey: "access_type",
        header: "Access",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.access_type ?? "—"}</span>
        ),
      },
      {
        accessorKey: "licence_status",
        header: "Licence",
        cell: ({ row }) => {
          const licence = row.original.licence_status;
          if (!licence) return <span className="text-sm text-gray-400">—</span>;
          const tone: BadgeTone =
            licence === "free"
              ? "success"
              : licence === "licensed"
                ? "info"
                : licence === "paywalled"
                  ? "danger"
                  : "neutral";
          return <StatusBadge tone={tone}>{licence}</StatusBadge>;
        },
      },
      {
        accessorKey: "is_active",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge tone={row.original.is_active ? "success" : "neutral"}>
            {row.original.is_active ? "Active" : "Inactive"}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const source = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Source actions"
                  className="rounded-lg p-1.5 transition-colors hover:bg-gray-100"
                >
                  <MoreVertical className="h-4 w-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSourceModal({ open: true, source })}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    mutateUpdateSource({
                      id: source.id,
                      data: { is_active: !source.is_active },
                    })
                  }
                >
                  {source.is_active ? "Set Inactive" : "Set Active"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeletingSource(source)}
                  className="text-red-600 focus:text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [mutateUpdateSource]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setPage(1); // re-sort from the first page
      return next;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={sourcesQuery.isPending}
          error={error}
          onRetry={() => sourcesQuery.refetch()}
          emptyMessage="No sources found"
          getRowId={(s) => s.id}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search sources…"
              />
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                  {(["all", "active"] as ActiveFilter[]).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => {
                        setActiveFilter(f);
                        setPage(1);
                      }}
                      className={cn(
                        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                        activeFilter === f
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-900"
                      )}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setSourceModal({ open: true })}
                  className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4" />
                  Add Source
                </button>
              </div>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>

      {sourceModal.open && (
        <SourceFormModal
          source={sourceModal.source}
          onClose={() => setSourceModal({ open: false })}
          onSaved={() => setPage(1)}
        />
      )}

      <DeleteSourceModal
        open={!!deletingSource}
        source={deletingSource}
        onClose={() => setDeletingSource(null)}
        onDeleted={() => setPage(1)}
      />
    </AdminLayout>
  );
}
